import "./index.scss";

import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import Button from "@/components/components/Button";
import { ButtonColor } from "@/components/components/Button/button.enum";
import Close from "@/components/components/Close";
import { Filter, FilterAppliedV2 } from "@/components/icons";
import ChatFilterContent from "@/messenger/components/ChatFilterContent";
import { conversationsSelectors } from "@/messenger/ducks/entities/conversations";
import { setFilterIsOpenAC, setFilterIsSmsCapableAC, setPrevFilterIsSmsCapableAC, setPrevSelectedCampaignNameAC, setPrevSelectedMonthAC, setSelectedCampaignNameAC, setSelectedNumberAC, setSelectedMonthAC, setHideTeamConversations, setPrevHideTeamConversations, setPrevSelectedNumberAC, setSelectedRepresentativeAC, setPrevSelectedRepresentativeAC } from "@/messenger/ducks/general/conversationRefinementsReducer.duck";
import { deviceTypeSelector, userSelector } from "@/messenger/ducks/selectors/general";
import { conversationRefinementsSelector, filterCounterSelector } from "@/messenger/selectors";
import { detectMobile } from "@/messenger/utils/deviceType";
import { getAllTopics } from "@/messenger/services/data.service";
import { TTopicNormalized } from "@/messenger/types/entities/topic";
import { TUserMetadata } from "@/messenger/routes/Settings";
import { representativesSelectors } from "@/messenger/ducks/entities/representatives";
import { fetchNumbers } from "@/messenger/utils/helpers";

interface IChatFilter {
    userMetadata: TUserMetadata;
}

const ChatFilter = (props: IChatFilter): React.ReactElement => {
    const { t } = useTranslation();

    const isMobile = detectMobile(useSelector(deviceTypeSelector));
    const [isShowTeamCount, setShowTeamCount] = useState<boolean>(false);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [myConversationsCount, setMyConversationsCount] = useState<number>(0);
    const [teamConversationsCount, setTeamConversationsCount] = useState<number>(0);

    const { selectedTopicName, prevSelectedTopicName, selectedMonth, selectedNumber, selectedRepresentative, prevSelectedRepresentative, prevSelectedMonth, isOpen, hideTeam, prevHideTeam, isSmsCapable, prevIsSmsCapable, prevSelectedNumber } = useSelector(conversationRefinementsSelector).filter;
    const textkitConversations = useSelector(conversationRefinementsSelector)?.result;

    const user = useSelector(userSelector);
    
    const representatives = useSelector(representativesSelectors.selectAll);
    

    // const totalCount = isShowTeamCount ? textkitConversationsCount + teamkitConversationsCount : textkitConversationsCount;

    const filterCount = useSelector(filterCounterSelector);

    const conversations = useSelector(conversationsSelectors.selectAll);
    const [numbers, setNumbers] = useState([]);

    const fetchOutboundNumbers = async (): Promise<any> => {

        try {
            const numbers: { outbound_address: string }[] = await fetchNumbers();

            if (numbers && numbers.length > 0) {
                setNumbers(numbers)
            }
        } catch (error) {
            console.log(error);
            return [];
        }
    }
    const [topics, setTopicOptions] = useState<TTopicNormalized[]>(null);

    const getTopics = async () => {
        const { data } = await getAllTopics();
        
        if (data?.topics) {
            const unsortedTopics = data?.topics;

            if (unsortedTopics) {
                const sortedTopics = unsortedTopics?.map(item => {
                    if (item) {
                        return ({
                            id: item?.id,
                            label: item?.name,
                            ariaLabel: item?.name,
                            heading: item?.name,
                        });
                     }
                })
                .sort((c1, c2) => c1?.label?.localeCompare(c2.label));

                setTopicOptions(sortedTopics);
            }
        }
    };

    useEffect(() => {
        if(!isShowTeamCount) {
            setTotalCount(myConversationsCount)
        } else {
            setTotalCount(myConversationsCount + teamConversationsCount)
        }
    }, [isShowTeamCount]);

    useEffect(() => {
        if(textkitConversations) {
            const myConversations = textkitConversations.filter(convo => convo.representativeId === user.identity)?.length;
            const teamConversations = textkitConversations.filter(convo => convo.representativeId !== user.identity)?.length;
            setMyConversationsCount(myConversations)
            setTeamConversationsCount(teamConversations)
        }
    }, [textkitConversations])


    useEffect(() => {
        fetchOutboundNumbers()
        getTopics();
    }, []);

    useEffect(() => {
        setShowTeamCount(props?.userMetadata?.teamConversationCount)
    }, [props?.userMetadata]);

    const dispatch = useDispatch();

    const clearFilters = (e?): void => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        dispatch(setHideTeamConversations(false));
        dispatch(setPrevHideTeamConversations(false));
        dispatch(setFilterIsOpenAC(false));
        dispatch(setSelectedCampaignNameAC(null));
        dispatch(setPrevSelectedCampaignNameAC(null));
        dispatch(setSelectedMonthAC(null));
        dispatch(setSelectedNumberAC(null));
        dispatch(setPrevSelectedNumberAC(null));
        dispatch(setSelectedRepresentativeAC(null));
        dispatch(setPrevSelectedRepresentativeAC(null));
        dispatch(setPrevSelectedMonthAC(null));
        dispatch(setPrevFilterIsSmsCapableAC(false));
        dispatch(setFilterIsSmsCapableAC(false));
    };

    const onSelectTopic = (name: string): void => {
        dispatch(setPrevSelectedCampaignNameAC(selectedTopicName));
        dispatch(setSelectedCampaignNameAC(name));
    };
    
    const onSelectNumber = (outboundAddress: string): void => {
        dispatch(setPrevSelectedNumberAC(selectedNumber));
        dispatch(setSelectedNumberAC(outboundAddress));
    };
    
    const onSelectRepresentative = (representative: string): void => {
        dispatch(setPrevSelectedRepresentativeAC(selectedRepresentative));
        dispatch(setSelectedRepresentativeAC(representative));
    };

    // const onSelectMonth = (month: string): void => {
    //     dispatch(setPrevSelectedMonthAC(selectedMonth));
    //     dispatch(setSelectedMonthAC(month));
    // };
    const onSelectHideTeam = (value: boolean): void => {
        dispatch(setPrevHideTeamConversations(hideTeam));
        dispatch(setHideTeamConversations(value));
    };

    const onFilterSmsCapable = (value: boolean): void => {
        dispatch(setPrevFilterIsSmsCapableAC(isSmsCapable));
        dispatch(setFilterIsSmsCapableAC(value));
    };

    const onChatFilterOpen = (): void => {
        dispatch(setFilterIsOpenAC(true));
    };

    const onApplyButtonClick = (): void => {
        dispatch(setPrevHideTeamConversations(hideTeam));
        dispatch(setFilterIsOpenAC(false));
        dispatch(setPrevSelectedCampaignNameAC(selectedTopicName));
        dispatch(setPrevSelectedMonthAC(selectedMonth));
        dispatch(setPrevFilterIsSmsCapableAC(isSmsCapable));
        dispatch(setPrevSelectedNumberAC(selectedNumber))
    };

    const onCancelButtonClick = (): void => {
        if (!prevSelectedNumber) {
            dispatch(setSelectedNumberAC(null));
            dispatch(setPrevSelectedNumberAC(null));
        }
        if (prevSelectedNumber && prevSelectedNumber !== selectedNumber) {
            dispatch(setSelectedNumberAC(prevSelectedNumber));
        }
        if (!prevSelectedRepresentative) {
            dispatch(setSelectedRepresentativeAC(null));
            dispatch(setPrevSelectedRepresentativeAC(null));
        }
        if (prevSelectedRepresentative && prevSelectedRepresentative !== selectedRepresentative) {
            dispatch(setSelectedRepresentativeAC(prevSelectedRepresentative));
        }
        if (!prevSelectedTopicName) {
            dispatch(setSelectedCampaignNameAC(null));
            dispatch(setPrevSelectedCampaignNameAC(null));
        }
        if (prevSelectedTopicName && prevSelectedTopicName !== selectedTopicName) {
            dispatch(setSelectedCampaignNameAC(prevSelectedTopicName));
        }
        if (!prevSelectedMonth) {
            dispatch(setSelectedMonthAC(null));
            dispatch(setPrevSelectedMonthAC(null));
        }

        if (prevSelectedMonth && prevSelectedMonth !== selectedMonth) {
            dispatch(setSelectedMonthAC(prevSelectedMonth));
        }
        if (prevIsSmsCapable !== isSmsCapable) {
            dispatch(setFilterIsSmsCapableAC(prevIsSmsCapable));
        }
        if (!prevHideTeam) {
            dispatch(setHideTeamConversations(false));
            dispatch(setPrevHideTeamConversations(false));
        }
        if (prevHideTeam && prevHideTeam !== hideTeam) {
            dispatch(setHideTeamConversations(prevHideTeam));
        }

        dispatch(setFilterIsOpenAC(false));
    };

    // const onClose = () => {
    //     dispatch(setFilterIsOpenAC(false));
    // };

    const renderFilterCounter = () => {
        return <div className={"c-chat-filter__counter"} data-testid="chat-filter-counter">
            {filterCount}
        </div>;
    };

    const filteredCampaigns = topics?.filter(c => c?.label !== "All");

    return (<>
        <div data-tip data-for={"CHAT_FILTER_BUTTON"}>  
            <Button 
                id={"CHAT_FILTER_BUTTON"} 
                ariaLabel="Chat Filter" 
                className={`c-chat-filter__icon ${filterCount > 0 ? "c-chat-filter__icon-background" : ""}`} 
                onClick={onChatFilterOpen}
                color={ButtonColor.SECONDARY}
                data-testid="chat-filter"
                toolTipText={t("Filter Conversations")}
            >
                {filterCount > 0 ? renderFilterCounter() : null}
                {selectedTopicName ? <FilterAppliedV2 /> :  <Filter width={20} height={20} />}
            </Button>
        </div>
        
        {isOpen && <Close isOpen={isOpen} closeChange={onCancelButtonClick}> 
            <ChatFilterContent
                topics={filteredCampaigns}
                numbers={numbers}
                representatives={[...representatives].sort((c1, c2) => c1?.identifier?.localeCompare(c2.identifier))}
                conversations={conversations}
                conversationCount={totalCount}
                selectedCampaignName={selectedTopicName}
                selectedMonth={selectedMonth}
                selectedNumber={selectedNumber}
                selectedRepresentative={selectedRepresentative}
                isSmsCapable={isSmsCapable}
                isMobile={isMobile}
                hideTeam={hideTeam}
                onHideTeam={onSelectHideTeam}
                onSelectCampaign={onSelectTopic}
                onSelectNumber={onSelectNumber}
                // onSelectMonth={onSelectMonth}
                onFilterSmsCapable={onFilterSmsCapable}
                onApplyButtonClick={onApplyButtonClick}
                onResetButtonClick={clearFilters}
                onCancelButtonClick={onCancelButtonClick}
                onSelectRepresentative={onSelectRepresentative}
            />;
        </Close>}
    </>);
};

export default ChatFilter;
